import { CheckIcon, XIcon } from "@phosphor-icons/react";
import { useTranslation } from "next-i18next";
import { FaCheck } from "react-icons/fa";
import { cn } from "../../../../../lib/utils";
import { Line } from "../../../casos/components/Line";
import { Subtitle } from "../../../casos/components/Subtitle";

function ComparisonWrapper({ children, title, className }) {
	return (
		<div className="relative flex-1 border border-white rounded-lg bg-background-01">
			<div
				className={cn(
					"flex items-center justify-center rounded-lg mx-auto",
					"max-w-[310px] h-[48px] w-full",
					"laptop:h-[64px] laptop:max-w-[380px]",
					"laptopL:max-w-[572px]",
					"top-0 absolute -translate-y-1/2 left-1/2 -translate-x-1/2",
					className,
				)}
			>
				<span className="text-inherit text-xl laptop:text-2xl font-bold leading-6 tracking-[0.06px]">
					{title}
				</span>
			</div>

			{children}
		</div>
	);
}

// Função auxiliar para construir itens com destaque
const buildHighlightedItem = (
	text,
	highlight,
	textEnd,
	highlightClass = "font-medium text-branding-03",
) => {
	const parts = [];

	if (text) parts.push(text);
	if (highlight)
		parts.push(`<span class="${highlightClass}">${highlight}</span>`);
	if (textEnd) parts.push(textEnd);

	return parts.join(" ");
};

export default function DevelopmentComparisonSection() {
	const { t } = useTranslation();
	const baseScenarioI18NKey =
		"products.squad-ia.developmentComparison.scenario.items";

	const baseSolutionI18NKey =
		"products.squad-ia.developmentComparison.solution.items";

	const scenarioItems = [
		buildHighlightedItem(
			t(`${baseScenarioI18NKey}.item01.text`),
			t(`${baseScenarioI18NKey}.item01.highlight`),
			t(`${baseScenarioI18NKey}.item01.textEnd`),
		),
		buildHighlightedItem(
			t(`${baseScenarioI18NKey}.item02.text`),
			t(`${baseScenarioI18NKey}.item02.highlight`),
			t(`${baseScenarioI18NKey}.item02.textEnd`),
		),
		buildHighlightedItem(
			t(`${baseScenarioI18NKey}.item03.text`),
			t(`${baseScenarioI18NKey}.item03.highlight`),
			t(`${baseScenarioI18NKey}.item03.textEnd`),
		),
		buildHighlightedItem(
			t(`${baseScenarioI18NKey}.item04.text`),
			t(`${baseScenarioI18NKey}.item04.highlight`),
			t(`${baseScenarioI18NKey}.item04.textEnd`),
		),
	];

	const solutionItems = [
		buildHighlightedItem(
			t(`${baseSolutionI18NKey}.item01.text`),
			t(`${baseSolutionI18NKey}.item01.highlight`),
			t(`${baseSolutionI18NKey}.item01.textEnd`),
			"font-semibold text-vivid-03",
		),
		buildHighlightedItem(
			t(`${baseSolutionI18NKey}.item02.text`),
			t(`${baseSolutionI18NKey}.item02.highlight`),
			t(`${baseSolutionI18NKey}.item02.textEnd`),
			"font-semibold text-vivid-03",
		),
		buildHighlightedItem(
			t(`${baseSolutionI18NKey}.item03.text`),
			t(`${baseSolutionI18NKey}.item03.highlight`),
			t(`${baseSolutionI18NKey}.item03.textEnd`),
			"font-semibold text-vivid-03",
		),
		buildHighlightedItem(
			t(`${baseSolutionI18NKey}.item04.text`),
			t(`${baseSolutionI18NKey}.item04.highlight`),
			t(`${baseSolutionI18NKey}.item04.textEnd`),
			"font-semibold text-vivid-03",
		),
	];

	return (
		<section className="text-white bg-neutral-07">
			<div className="container-blog">
				<div className="flex flex-col items-center justify-center">
					<Line className="border-vivid-03" />

					<Subtitle className="mt-4 mb-0 text-center text-white laptop:mt-6 laptop:mb-0">
						<h2>{t("products.squad-ia.developmentComparison.title")}</h2>
					</Subtitle>
				</div>

				<div
					className={cn(
						"flex flex-col laptop:items-stretch justify-center gap-16 laptop:gap-10 mt-[64px]",
						"laptop:flex-row laptopL:mt-[88px]",
					)}
				>
					<ComparisonWrapper
						title={t("products.squad-ia.developmentComparison.scenario.title")}
						className="text-white bg-branding-03"
					>
						<ul
							className={cn(
								"flex flex-col text-left",
								"laptop:gap-10 laptop:px-6 laptop:mb-12 laptop:mt-14",
								"gap-8 pl-6 pr-4 mb-4 mt-10",
							)}
						>
							{scenarioItems.map((item, index) => (
								<li
									key={index}
									className="flex items-center gap-2 laptop:gap-4"
								>
									<div className="p-[2px] w-[32px] h-[32px] flex items-center justify-center">
										<XIcon color="#00a4b2" size={32} weight="bold" />
									</div>

									<p
										className="text-lg text-white laptop:text-xl laptop:leading-7"
										dangerouslySetInnerHTML={{ __html: item }}
									/>
								</li>
							))}
						</ul>
					</ComparisonWrapper>

					<ComparisonWrapper
						title={t("products.squad-ia.developmentComparison.solution.title")}
						className="bg-vivid-03"
					>
						<ul
							className={cn(
								"flex flex-col text-left",
								"laptop:gap-6 laptop:px-6 laptop:mb-6 laptop:mt-14",
								"gap-8 pl-6 pr-4 mb-4 mt-10",
							)}
						>
							{solutionItems.map((item, index) => (
								<li
									key={index}
									className="flex items-center gap-2 laptop:gap-4"
								>
									<div className="p-[2px] w-[24px] h-[24px] flex items-center justify-center">
										<FaCheck size={24} className="fill-vivid-03" />
									</div>

									<p
										className="text-lg text-white laptop:text-xl laptop:leading-7"
										dangerouslySetInnerHTML={{ __html: item }}
									/>
								</li>
							))}
						</ul>
					</ComparisonWrapper>
				</div>
			</div>
		</section>
	);
}
